import {
  createAppSelector,
  createStringArraySelector,
  sectionSelectors,
  sectionUserSelectors,
  termSelectors,
} from "./base";
import { selectIsEditModeEnabled } from "./local";

import { type TermType } from "components/server-types";
import { RootState } from "store";
import { compactMap } from "utils/collections";

/** Term ids that have at least one section. */
const selectTermIdsWithSections = createStringArraySelector(
  [sectionSelectors.selectAll],
  (sections): string[] => [...new Set(compactMap(sections, (section) => section.term_id || null))],
);

/** Section ids for a term. */
export const selectSectionIdsForTerm = createStringArraySelector(
  [(_state: RootState, termId: string) => termId, sectionSelectors.selectAll],
  (termId, sections): string[] =>
    compactMap(sections, (section) => (section.term_id === termId ? section.id : null)),
);

/** Terms that have at least one section. */
const selectTermsWithSections = createAppSelector(
  [termSelectors.selectAll, selectTermIdsWithSections],
  (terms, termIdsWithSections): TermType[] => {
    const termIdSet = new Set(termIdsWithSections);
    return terms.filter((term) => termIdSet.has(term.id));
  },
);

/** All terms in edit mode; otherwise only terms that have sections. */
export const selectTermsForAdminContentView = createAppSelector(
  [selectIsEditModeEnabled, termSelectors.selectAll, selectTermsWithSections],
  (isEditModeEnabled, terms, termsWithSections): TermType[] =>
    isEditModeEnabled ? terms : termsWithSections,
);

export const selectTermHasSections = createAppSelector(
  [(state, termId: string) => termId, selectTermIdsWithSections],
  (termId, termIdsWithSections): boolean => termIdsWithSections.includes(termId),
);

/** Sorted term ids for the current user's sections. */
export const selectSortedTermIdsForCurrentUser = createStringArraySelector(
  [
    sectionUserSelectors.filterForCurrentUser,
    sectionSelectors.selectEntities,
    termSelectors.selectIds,
  ],
  (sectionUsers, sectionMap, termIds): string[] => {
    const termIdSet = new Set(
      compactMap(sectionUsers, (sectionUser) => {
        const section = sectionMap[sectionUser.section_id];
        return section?.term_id || null;
      }),
    );
    return termIds.filter((termId) => termIdSet.has(termId));
  },
);

export const selectSortedTermsForCurrentUser = createAppSelector(
  [termSelectors.selectAll, selectSortedTermIdsForCurrentUser],
  (terms, termIds): TermType[] => {
    const termIdSet = new Set(termIds);
    return terms.filter((term) => termIdSet.has(term.id));
  },
);

/** First sorted term id for the current user, or null if they have none. */
export const selectDefaultTermIdForCurrentUser = createAppSelector(
  [selectSortedTermIdsForCurrentUser],
  (termIds): string | null => termIds[0] ?? null,
);
